import { useState, useEffect, useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
const API_URL = process.env.REACT_APP_BACKEND_URL || "";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { DashboardSidebar } from "@/components/DashboardSidebar";
import { GraftonChat } from "@/components/GraftonChat";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { ScrollReveal } from "@/components/ScrollReveal";
import {
  CalendarDays,
  Plus,
  ChevronLeft,
  ChevronRight,
  Clock,
  MapPin,
  Sparkles,
  Brain,
  Target,
  Loader2,
  Trash2,
  Edit3,
  CalendarCheck,
  LayoutGrid,
  List,
  RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import {
  format,
  addDays,
  addWeeks,
  addMonths,
  addYears,
  subDays,
  subWeeks,
  subMonths,
  subYears,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfYear,
  endOfYear,
  eachDayOfInterval,
  eachHourOfInterval,
  isSameDay,
  isSameMonth,
  isToday,
  set,
  parseISO,
  getHours,
  getMinutes,
  differenceInMinutes,
  startOfDay,
  endOfDay,
} from "date-fns";
import { fr } from "date-fns/locale";

type ViewType = "day" | "week" | "month" | "year";

interface Appointment {
  id: string;
  title: string;
  description: string | null;
  start_time: string;
  end_time: string;
  location: string | null;
  category: string;
  color: string | null;
  is_all_day: boolean;
}

const CATEGORIES = [
  { value: "rdv", label: "Rendez-vous", icon: CalendarCheck, color: "hsl(38 90% 55%)" },
  { value: "call", label: "Appel", icon: Clock, color: "hsl(200 80% 55%)" },
  { value: "meeting", label: "Réunion", icon: LayoutGrid, color: "hsl(280 70% 60%)" },
  { value: "deadline", label: "Échéance", icon: Target, color: "hsl(0 70% 55%)" },
  { value: "personal", label: "Personnel", icon: CalendarDays, color: "hsl(150 60% 45%)" },
];

const getCategoryColor = (cat: string) => CATEGORIES.find(c => c.value === cat)?.color || "hsl(38 90% 55%)";

const HOURS = Array.from({ length: 24 }, (_, i) => i);

const AgendaDashboard = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [view, setView] = useState<ViewType>("week");
  const [currentDate, setCurrentDate] = useState(new Date());
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState<Appointment | null>(null);
  const [priorities, setPriorities] = useState<string[]>([]);
  const [prioritiesLoading, setPrioritiesLoading] = useState(false);

  // Form state
  const [formTitle, setFormTitle] = useState("");
  const [formDescription, setFormDescription] = useState("");
  const [formDate, setFormDate] = useState("");
  const [formStartTime, setFormStartTime] = useState("09:00");
  const [formEndTime, setFormEndTime] = useState("10:00");
  const [formLocation, setFormLocation] = useState("");
  const [formCategory, setFormCategory] = useState("rdv");
  const [formAllDay, setFormAllDay] = useState(false);

  useEffect(() => {
    if (!authLoading && !user) navigate("/auth");
  }, [authLoading, user, navigate]);

  const fetchAppointments = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/agenda/appointments`, { credentials: "include" });
      if (res.ok) setAppointments((await res.json()).appointments || []);
      else toast.error("Erreur de chargement de l'agenda");
    } catch { toast.error("Erreur réseau"); }
    setLoading(false);
  };

  useEffect(() => {
    if (user) fetchAppointments();
  }, [user]);

  const fetchPriorities = async () => {
    setPrioritiesLoading(true);
    try {
      const resp = await fetch(`${API_URL}/api/briefing/morning`, { credentials: "include" });
      if (resp.ok) {
        const data = await resp.json();
        setPriorities((data.priorities || []).map((p: any) => p.label));
      }
    } catch {
      toast.error("Impossible de charger les priorités IA.");
    } finally {
      setPrioritiesLoading(false);
    }
  };

  useEffect(() => {
    if (user && !loading) fetchPriorities();
  }, [user, loading]);

  const openNewAppointment = (date?: Date) => {
    setEditingAppointment(null);
    setFormTitle("");
    setFormDescription("");
    setFormDate(format(date || currentDate, "yyyy-MM-dd"));
    setFormStartTime("09:00");
    setFormEndTime("10:00");
    setFormLocation("");
    setFormCategory("rdv");
    setFormAllDay(false);
    setDialogOpen(true);
  };

  const openEditAppointment = (apt: Appointment) => {
    setEditingAppointment(apt);
    const start = parseISO(apt.start_time);
    const end = parseISO(apt.end_time);
    setFormTitle(apt.title);
    setFormDescription(apt.description || "");
    setFormDate(format(start, "yyyy-MM-dd"));
    setFormStartTime(format(start, "HH:mm"));
    setFormEndTime(format(end, "HH:mm"));
    setFormLocation(apt.location || "");
    setFormCategory(apt.category);
    setFormAllDay(apt.is_all_day);
    setDialogOpen(true);
  };

  const saveAppointment = async () => {
    if (!user || !formTitle.trim()) return;
    const startTime = formAllDay
      ? `${formDate}T00:00:00`
      : `${formDate}T${formStartTime}:00`;
    const endTime = formAllDay
      ? `${formDate}T23:59:59`
      : `${formDate}T${formEndTime}:00`;

    if (editingAppointment) {
      const res = await fetch(`${API_URL}/api/agenda/appointments/${editingAppointment.id}`, { method: "PUT", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ title: formTitle, description: formDescription || null, start_time: startTime, end_time: endTime, location: formLocation || null, category: formCategory, is_all_day: formAllDay }) });
      if (!res.ok) toast.error("Erreur de mise à jour");
      else toast.success("Rendez-vous modifié");
    } else {
      const res = await fetch(`${API_URL}/api/agenda/appointments`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ title: formTitle, description: formDescription || null, start_time: startTime, end_time: endTime, location: formLocation || null, category: formCategory, is_all_day: formAllDay }) });
      if (!res.ok) toast.error("Erreur de création");
      else toast.success("Rendez-vous créé");
    }
    setDialogOpen(false);
    fetchAppointments();
  };

  const deleteAppointment = async (id: string) => {
    const res = await fetch(`${API_URL}/api/agenda/appointments/${id}`, { method: "DELETE", credentials: "include" });
    if (!res.ok) toast.error("Erreur de suppression");
    else {
      toast.success("Rendez-vous supprimé");
      fetchAppointments();
    }
    setDialogOpen(false);
  };

  // Navigation
  const navigate_date = (dir: number) => {
    const fns = { day: dir > 0 ? addDays : subDays, week: dir > 0 ? addWeeks : subWeeks, month: dir > 0 ? addMonths : subMonths, year: dir > 0 ? addYears : subYears };
    setCurrentDate(fns[view](currentDate, 1));
  };

  const getTitle = () => {
    switch (view) {
      case "day": return format(currentDate, "EEEE d MMMM yyyy", { locale: fr });
      case "week": {
        const s = startOfWeek(currentDate, { weekStartsOn: 1 });
        const e = endOfWeek(currentDate, { weekStartsOn: 1 });
        return `${format(s, "d MMM", { locale: fr })} — ${format(e, "d MMM yyyy", { locale: fr })}`;
      }
      case "month": return format(currentDate, "MMMM yyyy", { locale: fr });
      case "year": return format(currentDate, "yyyy");
    }
  };

  // Filtered appointments for current view
  const visibleAppointments = useMemo(() => {
    let start: Date, end: Date;
    switch (view) {
      case "day": start = startOfDay(currentDate); end = endOfDay(currentDate); break;
      case "week": start = startOfWeek(currentDate, { weekStartsOn: 1 }); end = endOfWeek(currentDate, { weekStartsOn: 1 }); break;
      case "month": start = startOfMonth(currentDate); end = endOfMonth(currentDate); break;
      case "year": start = startOfYear(currentDate); end = endOfYear(currentDate); break;
    }
    return appointments.filter(a => {
      const d = parseISO(a.start_time);
      return d >= start && d <= end;
    });
  }, [appointments, currentDate, view]);

  if (authLoading) {
    return <div className="min-h-screen bg-background flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  }
  if (!user) return null;

  return (
    <SidebarProvider>
      <div className="min-h-screen bg-background flex w-full">
        <DashboardSidebar />
        <div className="flex-1 flex flex-col min-w-0">
          {/* Top bar */}
          <nav className="sticky top-0 z-50 border-b border-border/50 bg-background/80 backdrop-blur-xl">
            <div className="flex h-16 items-center justify-between px-4">
              <div className="flex items-center gap-3">
                <SidebarTrigger className="text-muted-foreground" />
                <div className="h-8 w-8 rounded-lg bg-primary flex items-center justify-center">
                  <CalendarDays className="h-4 w-4 text-primary-foreground" />
                </div>
                <span className="font-semibold text-lg tracking-tight">Agenda & Priorités</span>
              </div>
              <Button onClick={() => openNewAppointment()} className="gap-2">
                <Plus className="h-4 w-4" /> Nouveau rendez-vous
              </Button>
            </div>
          </nav>

          <main className="flex-1 px-4 md:px-8 py-6 space-y-6 overflow-auto">
            {/* View switcher + navigation */}
            <ScrollReveal>
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex items-center gap-2">
                  {(["day", "week", "month", "year"] as ViewType[]).map(v => (
                    <Button
                      key={v}
                      variant={view === v ? "default" : "outline"}
                      size="sm"
                      onClick={() => setView(v)}
                      className="capitalize"
                    >
                      {{ day: "Jour", week: "Semaine", month: "Mois", year: "Année" }[v]}
                    </Button>
                  ))}
                </div>
                <div className="flex items-center gap-3">
                  <Button variant="ghost" size="icon" onClick={() => navigate_date(-1)}>
                    <ChevronLeft className="h-5 w-5" />
                  </Button>
                  <h2 className="text-lg font-semibold capitalize min-w-[200px] text-center">{getTitle()}</h2>
                  <Button variant="ghost" size="icon" onClick={() => navigate_date(1)}>
                    <ChevronRight className="h-5 w-5" />
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setCurrentDate(new Date())}>
                    Aujourd'hui
                  </Button>
                </div>
              </div>
            </ScrollReveal>

            <div className="grid lg:grid-cols-[1fr_340px] gap-6">
              {/* Calendar area */}
              <ScrollReveal>
                <Card className="overflow-hidden">
                  <CardContent className="p-0">
                    {view === "day" && <DayView date={currentDate} appointments={visibleAppointments} onClickSlot={(d) => openNewAppointment(d)} onClickEvent={openEditAppointment} />}
                    {view === "week" && <WeekView date={currentDate} appointments={visibleAppointments} onClickSlot={(d) => openNewAppointment(d)} onClickEvent={openEditAppointment} />}
                    {view === "month" && <MonthView date={currentDate} appointments={visibleAppointments} onClickDay={(d) => { setCurrentDate(d); setView("day"); }} onClickEvent={openEditAppointment} />}
                    {view === "year" && <YearView date={currentDate} appointments={appointments} onClickMonth={(d) => { setCurrentDate(d); setView("month"); }} />}
                  </CardContent>
                </Card>
              </ScrollReveal>

              {/* Right sidebar: Priorities + upcoming */}
              <div className="space-y-6">
                <ScrollReveal delay={100}>
                  <Card className="border-primary/20 shadow-[0_4px_24px_hsl(var(--glow-amber)/0.06)]">
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-base flex items-center gap-2">
                          <Sparkles className="h-5 w-5 text-primary" />
                          Priorités Grafton IA
                        </CardTitle>
                        <Button variant="ghost" size="sm" onClick={fetchPriorities} disabled={prioritiesLoading}>
                          <RefreshCw className={`h-3.5 w-3.5 ${prioritiesLoading ? "animate-spin" : ""}`} />
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {prioritiesLoading ? (
                        <div className="space-y-2">
                          {[1, 2, 3].map(i => <div key={i} className="h-12 bg-secondary/50 rounded-lg animate-pulse" />)}
                        </div>
                      ) : priorities.length > 0 ? (
                        <div className="space-y-2">
                          {priorities.map((p, i) => (
                            <div key={i} className="flex items-start gap-2.5 p-3 rounded-lg bg-secondary/40 border border-border/50">
                              <Brain className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                              <p className="text-xs leading-relaxed">{p}</p>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-xs text-muted-foreground">Ajoutez des rendez-vous pour recevoir des conseils.</p>
                      )}
                    </CardContent>
                  </Card>
                </ScrollReveal>

                <ScrollReveal delay={200}>
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base flex items-center gap-2">
                        <List className="h-5 w-5 text-primary" />
                        Prochains rendez-vous
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2 max-h-[300px] overflow-y-auto">
                        {appointments
                          .filter(a => parseISO(a.start_time) >= startOfDay(new Date()))
                          .slice(0, 8)
                          .map(apt => (
                            <button
                              key={apt.id}
                              onClick={() => openEditAppointment(apt)}
                              className="w-full text-left p-3 rounded-lg bg-secondary/30 border border-border/50 hover:border-primary/30 transition-colors"
                            >
                              <div className="flex items-center gap-2 mb-1">
                                <div className="h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: getCategoryColor(apt.category) }} />
                                <p className="text-sm font-medium truncate">{apt.title}</p>
                              </div>
                              <div className="flex items-center gap-3 text-xs text-muted-foreground">
                                <span className="flex items-center gap-1">
                                  <CalendarDays className="h-3 w-3" />
                                  {format(parseISO(apt.start_time), "d MMM", { locale: fr })}
                                </span>
                                {!apt.is_all_day && (
                                  <span className="flex items-center gap-1">
                                    <Clock className="h-3 w-3" />
                                    {format(parseISO(apt.start_time), "HH:mm")} – {format(parseISO(apt.end_time), "HH:mm")}
                                  </span>
                                )}
                              </div>
                            </button>
                          ))}
                        {appointments.filter(a => parseISO(a.start_time) >= startOfDay(new Date())).length === 0 && (
                          <p className="text-xs text-muted-foreground text-center py-4">Aucun rendez-vous à venir</p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </ScrollReveal>
              </div>
            </div>

            {/* Grafton Chat at bottom */}
            <ScrollReveal delay={100}>
              <Card className="border-primary/20">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-primary" />
                    Grafton IA — Assistant Agenda
                  </CardTitle>
                  <CardDescription>Demandez à Grafton de planifier, réorganiser ou optimiser votre emploi du temps</CardDescription>
                </CardHeader>
                <CardContent>
                  <GraftonChat
                    variant="agenda"
                    context={{
                      type: "agenda",
                      appointments: appointments.slice(0, 15).map(a => ({
                        title: a.title,
                        start: a.start_time,
                        end: a.end_time,
                        category: a.category,
                      })),
                      today: format(new Date(), "yyyy-MM-dd"),
                    }}
                    quickPrompts={[
                      "Optimise mon planning de la semaine",
                      "Quels rendez-vous sont prioritaires ?",
                      "Propose un créneau pour un prospect",
                      "Analyse mes temps morts",
                    ]}
                    subtitle="Assistant agenda"
                    welcomeMessage="Je peux vous aider à organiser, prioriser et optimiser votre agenda professionnel."
                  />
                </CardContent>
              </Card>
            </ScrollReveal>
          </main>
        </div>
      </div>

      {/* Appointment dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarCheck className="h-5 w-5 text-primary" />
              {editingAppointment ? "Modifier le rendez-vous" : "Nouveau rendez-vous"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs text-muted-foreground mb-1 block">Titre</Label>
              <Input value={formTitle} onChange={e => setFormTitle(e.target.value)} placeholder="Ex: RDV client Dupont" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground mb-1 block">Catégorie</Label>
              <Select value={formCategory} onValueChange={setFormCategory}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(c => (
                    <SelectItem key={c.value} value={c.value}>
                      <span className="flex items-center gap-2">
                        <span className="h-2 w-2 rounded-full" style={{ backgroundColor: c.color }} />
                        {c.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground mb-1 block">Date</Label>
              <Input type="date" value={formDate} onChange={e => setFormDate(e.target.value)} />
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={formAllDay} onCheckedChange={setFormAllDay} id="allday" />
              <Label htmlFor="allday" className="text-sm">Journée entière</Label>
            </div>
            {!formAllDay && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-muted-foreground mb-1 block">Début</Label>
                  <Input type="time" value={formStartTime} onChange={e => setFormStartTime(e.target.value)} />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground mb-1 block">Fin</Label>
                  <Input type="time" value={formEndTime} onChange={e => setFormEndTime(e.target.value)} />
                </div>
              </div>
            )}
            <div>
              <Label className="text-xs text-muted-foreground mb-1 block">Lieu</Label>
              <Input value={formLocation} onChange={e => setFormLocation(e.target.value)} placeholder="Ex: Bureau, Zoom, ..." />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground mb-1 block">Description</Label>
              <Textarea value={formDescription} onChange={e => setFormDescription(e.target.value)} placeholder="Notes..." rows={3} />
            </div>
          </div>
          <DialogFooter className="flex justify-between">
            <div>
              {editingAppointment && (
                <Button variant="destructive" size="sm" onClick={() => deleteAppointment(editingAppointment.id)}>
                  <Trash2 className="h-4 w-4 mr-1" /> Supprimer
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
              <Button onClick={saveAppointment} disabled={!formTitle.trim()}>
                {editingAppointment ? "Enregistrer" : "Créer"}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SidebarProvider>
  );
};

// ─── Day View ───────────────────────────────────────────────
const DayView = ({ date, appointments, onClickSlot, onClickEvent }: {
  date: Date;
  appointments: Appointment[];
  onClickSlot: (d: Date) => void;
  onClickEvent: (a: Appointment) => void;
}) => (
  <div className="overflow-y-auto max-h-[600px]">
    {HOURS.map(hour => {
      const hourAppts = appointments.filter(a => {
        const h = getHours(parseISO(a.start_time));
        return h === hour && !a.is_all_day;
      });
      return (
        <div
          key={hour}
          className="flex border-b border-border/30 min-h-[60px] hover:bg-secondary/20 transition-colors cursor-pointer group"
          onClick={() => onClickSlot(set(date, { hours: hour, minutes: 0 }))}
        >
          <div className="w-16 shrink-0 py-2 px-3 text-xs text-muted-foreground font-medium border-r border-border/30">
            {String(hour).padStart(2, "0")}:00
          </div>
          <div className="flex-1 p-1 space-y-1">
            {hourAppts.map(a => (
              <button
                key={a.id}
                onClick={e => { e.stopPropagation(); onClickEvent(a); }}
                className="w-full text-left px-3 py-1.5 rounded-md text-xs font-medium text-white truncate transition-transform hover:scale-[1.01]"
                style={{ backgroundColor: getCategoryColor(a.category) }}
              >
                {format(parseISO(a.start_time), "HH:mm")} — {a.title}
              </button>
            ))}
          </div>
        </div>
      );
    })}
  </div>
);

// ─── Week View ──────────────────────────────────────────────
const WeekView = ({ date, appointments, onClickSlot, onClickEvent }: {
  date: Date;
  appointments: Appointment[];
  onClickSlot: (d: Date) => void;
  onClickEvent: (a: Appointment) => void;
}) => {
  const weekStart = startOfWeek(date, { weekStartsOn: 1 });
  const days = eachDayOfInterval({ start: weekStart, end: addDays(weekStart, 6) });

  return (
    <div className="overflow-auto">
      {/* Header */}
      <div className="flex border-b border-border/50 sticky top-0 bg-card z-10">
        <div className="w-16 shrink-0 border-r border-border/30" />
        {days.map(d => (
          <div
            key={d.toISOString()}
            className={`flex-1 min-w-[100px] text-center py-3 border-r border-border/30 last:border-r-0 ${isToday(d) ? "bg-primary/5" : ""}`}
          >
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{format(d, "EEE", { locale: fr })}</p>
            <p className={`text-lg font-bold ${isToday(d) ? "text-primary" : ""}`}>{format(d, "d")}</p>
          </div>
        ))}
      </div>
      {/* Time grid */}
      <div className="max-h-[550px] overflow-y-auto">
        {HOURS.filter(h => h >= 7 && h <= 21).map(hour => (
          <div key={hour} className="flex border-b border-border/20 min-h-[50px]">
            <div className="w-16 shrink-0 py-1 px-2 text-[10px] text-muted-foreground border-r border-border/30">
              {String(hour).padStart(2, "0")}:00
            </div>
            {days.map(d => {
              const dayAppts = appointments.filter(a => {
                const s = parseISO(a.start_time);
                return isSameDay(s, d) && getHours(s) === hour && !a.is_all_day;
              });
              return (
                <div
                  key={d.toISOString()}
                  className="flex-1 min-w-[100px] border-r border-border/20 last:border-r-0 p-0.5 hover:bg-secondary/20 cursor-pointer transition-colors"
                  onClick={() => onClickSlot(set(d, { hours: hour, minutes: 0 }))}
                >
                  {dayAppts.map(a => (
                    <button
                      key={a.id}
                      onClick={e => { e.stopPropagation(); onClickEvent(a); }}
                      className="w-full text-left px-1.5 py-1 rounded text-[10px] font-medium text-white truncate mb-0.5"
                      style={{ backgroundColor: getCategoryColor(a.category) }}
                    >
                      {a.title}
                    </button>
                  ))}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── Month View ─────────────────────────────────────────────
const MonthView = ({ date, appointments, onClickDay, onClickEvent }: {
  date: Date;
  appointments: Appointment[];
  onClickDay: (d: Date) => void;
  onClickEvent: (a: Appointment) => void;
}) => {
  const monthStart = startOfMonth(date);
  const monthEnd = endOfMonth(date);
  const calStart = startOfWeek(monthStart, { weekStartsOn: 1 });
  const calEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
  const days = eachDayOfInterval({ start: calStart, end: calEnd });

  return (
    <div>
      {/* Day headers */}
      <div className="grid grid-cols-7 border-b border-border/50">
        {["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"].map(d => (
          <div key={d} className="text-center py-2 text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
            {d}
          </div>
        ))}
      </div>
      {/* Days grid */}
      <div className="grid grid-cols-7">
        {days.map(d => {
          const dayAppts = appointments.filter(a => isSameDay(parseISO(a.start_time), d));
          const inMonth = isSameMonth(d, date);
          return (
            <div
              key={d.toISOString()}
              className={`min-h-[90px] border-b border-r border-border/20 p-1.5 cursor-pointer hover:bg-secondary/20 transition-colors ${!inMonth ? "opacity-30" : ""} ${isToday(d) ? "bg-primary/5" : ""}`}
              onClick={() => onClickDay(d)}
            >
              <p className={`text-xs font-medium mb-1 ${isToday(d) ? "text-primary font-bold" : ""}`}>{format(d, "d")}</p>
              <div className="space-y-0.5">
                {dayAppts.slice(0, 3).map(a => (
                  <button
                    key={a.id}
                    onClick={e => { e.stopPropagation(); onClickEvent(a); }}
                    className="w-full text-left px-1.5 py-0.5 rounded text-[9px] font-medium text-white truncate"
                    style={{ backgroundColor: getCategoryColor(a.category) }}
                  >
                    {a.title}
                  </button>
                ))}
                {dayAppts.length > 3 && (
                  <p className="text-[9px] text-muted-foreground pl-1">+{dayAppts.length - 3} autres</p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─── Year View ──────────────────────────────────────────────
const YearView = ({ date, appointments, onClickMonth }: {
  date: Date;
  appointments: Appointment[];
  onClickMonth: (d: Date) => void;
}) => {
  const months = Array.from({ length: 12 }, (_, i) => new Date(date.getFullYear(), i, 1));
  return (
    <div className="grid grid-cols-3 sm:grid-cols-4 gap-4 p-6">
      {months.map(m => {
        const monthAppts = appointments.filter(a => {
          const d = parseISO(a.start_time);
          return d.getMonth() === m.getMonth() && d.getFullYear() === m.getFullYear();
        });
        const isCurrent = m.getMonth() === new Date().getMonth() && m.getFullYear() === new Date().getFullYear();
        return (
          <button
            key={m.toISOString()}
            onClick={() => onClickMonth(m)}
            className={`p-4 rounded-xl border transition-all hover:border-primary/40 hover:shadow-md text-center ${isCurrent ? "border-primary/30 bg-primary/5" : "border-border/50 bg-secondary/20"}`}
          >
            <p className={`text-sm font-semibold capitalize mb-2 ${isCurrent ? "text-primary" : ""}`}>
              {format(m, "MMMM", { locale: fr })}
            </p>
            <div className="flex items-center justify-center gap-1.5">
              <CalendarCheck className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="text-xs text-muted-foreground">{monthAppts.length} rdv</span>
            </div>
          </button>
        );
      })}
    </div>
  );
};

export default AgendaDashboard;
